import React from 'react';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Check, X, Smartphone, Server, Thermometer, Battery, Activity } from 'lucide-react';
interface ProduktProps {
  onNavigate: (page: string) => void;
}
export function Produkt({
  onNavigate
}: ProduktProps) {
  return <div className="pb-20" data-id="element-391">
      {/* Hero */}
      <section className="pt-20 pb-16 bg-[#0f0f16]" data-id="element-392">
        <div className="container mx-auto px-4 md:px-6 text-center" data-id="element-393">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6" data-id="element-394">
            Technische Spezifikationen
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8" data-id="element-395">
            Detaillierte Übersicht der Funktionen, Hardware-Parameter und
            App-Features.
          </p>
        </div>
      </section>

      {/* Function Matrix */}
      <section className="py-16 bg-[#0a0a0f]" data-id="element-396">
        <div className="container mx-auto px-4 md:px-6" data-id="element-397">
          <SectionHeading title="Funktionsübersicht: BLE vs. 4G" align="center" data-id="element-398" />

          <div className="overflow-x-auto" data-id="element-399">
            <table className="w-full max-w-4xl mx-auto border-collapse" data-id="element-400">
              <thead data-id="element-401">
                <tr className="border-b border-gray-800" data-id="element-402">
                  <th className="py-4 px-6 text-left text-gray-400 font-medium" data-id="element-403">
                    Funktion
                  </th>
                  <th className="py-4 px-6 text-center text-blue-400 font-bold bg-blue-500/5 rounded-t-lg" data-id="element-404">
                    Bluetooth (BLE)
                  </th>
                  <th className="py-4 px-6 text-center text-cyan-400 font-bold bg-cyan-500/5 rounded-t-lg" data-id="element-405">
                    4G Remote
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-300" data-id="element-406">
                {[{
                name: 'Türen öffnen/schließen',
                ble: true,
                g4: true
              }, {
                name: 'Motor Start/Stopp',
                ble: true,
                g4: true
              }, {
                name: 'Kofferraum öffnen',
                ble: true,
                g4: true
              }, {
                name: 'Auto Lock/Unlock (Smart Access)',
                ble: true,
                g4: false
              }, {
                name: 'Offline-Nutzung (Tiefgarage)',
                ble: true,
                g4: false
              }, {
                name: 'Standortortung (GPS)',
                ble: false,
                g4: true
              }, {
                name: 'Klima Fernsteuerung',
                ble: false,
                g4: true
              }, {
                name: 'Fahrzeugstatus Fernabfrage',
                ble: false,
                g4: true
              }].map((row, i) => <tr key={i} className="border-b border-gray-800 hover:bg-white/5 transition-colors" data-id="element-407">
                    <td className="py-4 px-6 font-medium" data-id="element-408">{row.name}</td>
                    <td className="py-4 px-6 text-center bg-blue-500/5" data-id="element-409">
                      {row.ble ? <Check className="inline w-5 h-5 text-green-500" data-id="element-410" /> : <X className="inline w-5 h-5 text-gray-600" data-id="element-411" />}
                    </td>
                    <td className="py-4 px-6 text-center bg-cyan-500/5" data-id="element-412">
                      {row.g4 ? <Check className="inline w-5 h-5 text-green-500" data-id="element-413" /> : <X className="inline w-5 h-5 text-gray-600" data-id="element-414" />}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Technical Data */}
      <section className="py-16 bg-[#0f0f16]" data-id="element-415">
        <div className="container mx-auto px-4 md:px-6" data-id="element-416">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-417">
            <div data-id="element-418">
              <SectionHeading title="Hardware & Performance" subtitle="Entwickelt für extreme Bedingungen und höchste Zuverlässigkeit." data-id="element-419" />
              <div className="space-y-6 mt-8" data-id="element-420">
                <div className="flex items-start gap-4" data-id="element-421">
                  <Activity className="w-6 h-6 text-cyan-500 mt-1" data-id="element-422" />
                  <div data-id="element-423">
                    <h4 className="text-white font-bold" data-id="element-424">Reaktionszeiten</h4>
                    <p className="text-gray-400 text-sm" data-id="element-425">
                      Bluetooth: &lt; 200ms (typisch)
                      <br data-id="element-426" />
                      4G Remote: &lt; 1-2s (netzabhängig)
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-427">
                  <Thermometer className="w-6 h-6 text-cyan-500 mt-1" data-id="element-428" />
                  <div data-id="element-429">
                    <h4 className="text-white font-bold" data-id="element-430">Betriebsumgebung</h4>
                    <p className="text-gray-400 text-sm" data-id="element-431">
                      Temperatur: -30°C bis +85°C
                      <br data-id="element-432" />
                      Feuchtigkeit: 5% bis 95% RH
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-433">
                  <Battery className="w-6 h-6 text-cyan-500 mt-1" data-id="element-434" />
                  <div data-id="element-435">
                    <h4 className="text-white font-bold" data-id="element-436">Stromversorgung</h4>
                    <p className="text-gray-400 text-sm" data-id="element-437">
                      Spannung: 9V - 16V DC (12V System)
                      <br data-id="element-438" />
                      Ruhestrom: &lt; 5mA (Ultra Low Power)
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative h-80 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border border-gray-700 flex items-center justify-center overflow-hidden" data-id="element-439">
              <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-20" data-id="element-440" />
              <Server className="w-24 h-24 text-cyan-500/50 relative z-10" data-id="element-441" />
              <div className="absolute bottom-4 right-4 text-xs text-gray-500 font-mono" data-id="element-442">
                TIKA-HW-V3.0
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* App Showcase */}
      <section className="py-16 bg-[#0a0a0f]" data-id="element-443">
        <div className="container mx-auto px-4 md:px-6 text-center" data-id="element-444">
          <SectionHeading title="Die App: Dein Cockpit" align="center" data-id="element-445" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12" data-id="element-446">
            {[{
            title: 'Remote Control',
            img: 'https://cdn.magicpatterns.com/uploads/xBiCwszHxUToH7YZWzJ8bp/_1_.png',
            contain: true
          }, {
            title: 'GPS - Ortung',
            img: 'https://cdn.magicpatterns.com/uploads/8AoiLeUhJkWk4MukbbdGQh/_2_.png',
            contain: true
          }, {
            title: 'Key Sharing',
            img: 'https://cdn.magicpatterns.com/uploads/oQ99NcN28ZR3y8V9prrnf1/_3_.png',
            contain: true
          }].map((item, i) => <Card key={i} className="p-0 overflow-hidden group" data-id="element-447">
                <div className="h-64 overflow-hidden relative bg-gray-100" data-id="element-448">
                  <img src={item.img} alt={item.title} className={`w-full h-full transition-transform duration-500 group-hover:scale-105 ${item.contain ? 'object-contain' : 'object-cover'}`} data-id="element-449" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] to-transparent opacity-60" data-id="element-450" />
                  <div className="absolute bottom-4 left-4 font-bold text-xl text-white" data-id="element-451">
                    {item.title}
                  </div>
                </div>
              </Card>)}
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 text-center mt-12" data-id="element-452">
        <Button onClick={() => onNavigate('contact')} size="lg" data-id="element-453">
          Jetzt Demo anfragen
        </Button>
      </div>
    </div>;
}